import { Fragment, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Pencil, Check, X, RotateCcw, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

// Onglet "Liste des ventes" (réservé aux administrateurs, voir
// StatistiquesPage.jsx) : détail vente par vente des chiffres pris en compte
// dans les statistiques. Chaque vente se déplie (flèche) pour voir chacun de
// ses articles et corriger son coût d'achat fournisseur INDÉPENDAMMENT des
// autres lignes du même ticket (ex. une prestation libre sans prix d'achat
// connu, sans toucher aux autres articles vendus dans le même ticket) — la
// correction n'affecte jamais le ticket lui-même, seulement le calcul du
// bénéfice/de la marge dans les statistiques (voir backend, PATCH
// /stats/liste/{id}/items/{index}/cout). Le chiffre d'affaires ne peut
// jamais être modifié.
export function ListeVentesTab({ periode, customDebut, customFin, useCustom }) {
  const [lignes, setLignes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ouvertes, setOuvertes] = useState({});
  const [editing, setEditing] = useState(null); // { ticketId, itemIndex } | null
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = useCustom && (customDebut || customFin)
        ? { debut: customDebut || undefined, fin: customFin || undefined }
        : { periode };
      const { data } = await api.get("/stats/liste", { params });
      setLignes(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [periode, customDebut, customFin, useCustom]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOuvert = (ticketId) => {
    setOuvertes((o) => ({ ...o, [ticketId]: !o[ticketId] }));
  };

  const commencerEdition = (ticketId, item) => {
    setEditing({ ticketId, itemIndex: item.item_index });
    setEditValue(String(item.cout_ht_unitaire));
  };

  const annulerEdition = () => {
    setEditing(null);
    setEditValue("");
  };

  const validerEdition = async (ticketId) => {
    const valeur = parseFloat(editValue.replace(",", "."));
    if (isNaN(valeur) || valeur < 0) {
      toast.error("Indiquez un coût valide (0 ou plus)");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/stats/liste/${ticketId}/items/${editing.itemIndex}/cout`, { cout_ht: valeur });
      toast.success("Coût d'achat corrigé pour les statistiques");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const retirerCorrection = async (ticketId, item) => {
    if (!window.confirm("Revenir au coût calculé automatiquement pour cet article ?")) return;
    try {
      await api.delete(`/stats/liste/${ticketId}/items/${item.item_index}/cout`);
      toast.success("Correction retirée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Chargement...</p>;

  return (
    <div data-testid="liste-ventes-tab">
      <p className="text-xs text-muted-foreground mb-3">
        Détail de chaque vente prise en compte dans les statistiques de la période sélectionnée ci-dessus.
        Dépliez une vente (flèche) pour voir ses articles et corriger le coût d'achat fournisseur de chacun
        indépendamment — utile quand il n'a pas pu être renseigné (ex. prestation libre). Cette correction
        n'affecte jamais le ticket lui-même, seulement le calcul du bénéfice et de la marge. Le chiffre
        d'affaires, lui, ne peut jamais être modifié.
      </p>
      <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="liste-ventes-table">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-secondary text-left">
            <tr>
              <th className="p-3 w-8"></th>
              <th className="p-3">Date</th>
              <th className="p-3">Vente</th>
              <th className="p-3">Vendeur</th>
              <th className="p-3 text-right">CA généré (TTC)</th>
              <th className="p-3 text-right">Coût d'achat fournisseur (H.T)</th>
              <th className="p-3 text-right">Bénéfice réel (H.T)</th>
              <th className="p-3 text-right">Marge</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((l) => {
              const ouvert = !!ouvertes[l.ticket_id];
              return (
                <Fragment key={l.ticket_id}>
                  <tr
                    className="border-t border-border cursor-pointer hover:bg-secondary/40"
                    onClick={() => toggleOuvert(l.ticket_id)} data-testid={`liste-ventes-row-${l.ticket_id}`}
                  >
                    <td className="p-3">
                      {ouvert ? <ChevronDown size={15} className="text-muted-foreground" /> : <ChevronRight size={15} className="text-muted-foreground" />}
                    </td>
                    <td className="p-3 whitespace-nowrap">{new Date(l.date).toLocaleDateString("fr-FR")}</td>
                    <td className="p-3">{l.numero}</td>
                    <td className="p-3">{l.vendeur_nom}</td>
                    <td className="p-3 text-right font-medium">{l.ca_ttc.toFixed(2)} €</td>
                    <td className="p-3 text-right">
                      <span data-testid={`liste-ventes-cout-${l.ticket_id}`}>{l.cout_ht.toFixed(2)} €</span>
                      {l.cout_corrige && (
                        <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 font-medium">corrigé</span>
                      )}
                    </td>
                    <td className="p-3 text-right text-emerald-600 font-medium">{l.benefice.toFixed(2)} €</td>
                    <td className="p-3 text-right text-muted-foreground">{l.marge_pct != null ? `${l.marge_pct.toFixed(1)}%` : "—"}</td>
                  </tr>

                  {ouvert && (
                    <tr key={`${l.ticket_id}-detail`} className="border-t border-border bg-secondary/20">
                      <td colSpan={8} className="p-0">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-muted-foreground">
                              <th className="py-2 pl-12 text-left font-medium">Article</th>
                              <th className="py-2 text-right font-medium">Qté</th>
                              <th className="py-2 text-right font-medium">CA (TTC)</th>
                              <th className="py-2 text-right font-medium pr-4">Coût d'achat (H.T, unitaire)</th>
                              <th className="py-2 text-right font-medium">Bénéfice</th>
                              <th className="py-2 pr-3"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {l.items.map((item) => {
                              const enEdition = editing && editing.ticketId === l.ticket_id && editing.itemIndex === item.item_index;
                              return (
                                <tr key={item.item_index} className="border-t border-border/60" data-testid={`liste-ventes-item-${l.ticket_id}-${item.item_index}`}>
                                  <td className="py-2 pl-12">{item.nom}</td>
                                  <td className="py-2 text-right">{item.quantite}</td>
                                  <td className="py-2 text-right">{item.ca_ttc.toFixed(2)} €</td>
                                  <td className="py-2 text-right pr-4">
                                    {enEdition ? (
                                      <div className="flex items-center justify-end gap-1.5">
                                        <Input
                                          autoFocus type="text" inputMode="decimal" value={editValue}
                                          onChange={(e) => setEditValue(e.target.value)}
                                          onClick={(e) => e.stopPropagation()}
                                          onKeyDown={(e) => { if (e.key === "Enter") validerEdition(l.ticket_id); if (e.key === "Escape") annulerEdition(); }}
                                          className="w-20 h-7 text-right text-xs" data-testid={`liste-ventes-item-input-${l.ticket_id}-${item.item_index}`}
                                        />
                                        <button onClick={(e) => { e.stopPropagation(); validerEdition(l.ticket_id); }} disabled={saving} title="Valider" data-testid={`liste-ventes-item-valider-${l.ticket_id}-${item.item_index}`}>
                                          <Check size={14} className="text-emerald-600" />
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); annulerEdition(); }} title="Annuler">
                                          <X size={14} className="text-muted-foreground" />
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="flex items-center justify-end gap-1.5">
                                        <span>{item.cout_ht_unitaire.toFixed(2)} €</span>
                                        {item.cout_corrige && (
                                          <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-600 font-medium" title={`Coût auto : ${item.cout_ht_unitaire_auto.toFixed(2)} €`}>
                                            corrigé
                                          </span>
                                        )}
                                        <button onClick={(e) => { e.stopPropagation(); commencerEdition(l.ticket_id, item); }} title="Corriger" data-testid={`liste-ventes-item-editer-${l.ticket_id}-${item.item_index}`}>
                                          <Pencil size={12} className="text-muted-foreground hover:text-primary" />
                                        </button>
                                        {item.cout_corrige && (
                                          <button onClick={(e) => { e.stopPropagation(); retirerCorrection(l.ticket_id, item); }} title="Revenir au calcul automatique" data-testid={`liste-ventes-item-reset-${l.ticket_id}-${item.item_index}`}>
                                            <RotateCcw size={12} className="text-muted-foreground hover:text-destructive" />
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td className="py-2 text-right text-emerald-600">{item.benefice.toFixed(2)} €</td>
                                  <td className="py-2 pr-3"></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {lignes.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-sm text-muted-foreground">Aucune vente sur cette période.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
